import { useCallback, useEffect, useRef, useState } from "react";

type TimelineIdleFadeInput = {
  /** The timeline is on screen and was not asked for, so it should not outstay the scroll. */
  enabled: boolean;
  /** Called when the fade has finished; hide the timeline here. */
  onFaded: () => void;
};

export type TimelineIdleFade = {
  /** The timeline is fading out now. */
  fading: boolean;
  /** Scrolling happened: hold the timeline for another full pause. */
  noteActivity: () => void;
  /** The pointer is over the timeline, or has left it: it waits while hovered, and pauses afresh after. */
  hover: (hovering: boolean) => void;
  /** The user pressed in the timeline, focused into it or picked a date: it stays until dismissed. */
  engage: () => void;
};

/** How long the timeline stays after the scrolling stops. */
export const TIMELINE_IDLE_MS = 1000;
/** How long it takes to fade; matches the overlay's opacity transition. */
export const TIMELINE_FADE_MS = 300;

/**
 * Fades out a timeline that came up because the user grabbed the scrollbar, the way an overlay
 * scrollbar fades, while one opened with its toggle stays until it is dismissed.
 *
 * Scrolling restarts the pause. The timeline sits right beside the scrollbar, so a hand letting go
 * of the thumb crosses it on the way back to the messages: hovering therefore only holds the fade,
 * and leaving starts a fresh pause. Pressing in it, focusing into it or picking a date means the user
 * wants it, so from then on it no longer fades. The timers live in refs rather than state so that
 * scrolling, which fires many times a second, does not re-render the chat window.
 */
export default function useTimelineIdleFade(input: Readonly<TimelineIdleFadeInput>): TimelineIdleFade {
  const { enabled, onFaded } = input;
  const [fading, setFading] = useState(false);
  const enabledRef = useRef(enabled);
  const engagedRef = useRef(false);
  const hoveringRef = useRef(false);
  const onFadedRef = useRef(onFaded);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const clearTimers = useCallback(() => {
    clearTimeout(idleTimer.current);
    clearTimeout(fadeTimer.current);
  }, []);

  const startPause = useCallback(() => {
    clearTimers();
    idleTimer.current = setTimeout(() => {
      setFading(true);
      fadeTimer.current = setTimeout(() => {
        setFading(false);
        onFadedRef.current();
      }, TIMELINE_FADE_MS);
    }, TIMELINE_IDLE_MS);
  }, [clearTimers]);

  useEffect(() => {
    onFadedRef.current = onFaded;
  }, [onFaded]);

  // Each time the timeline comes up unasked, it starts a fresh pause and forgets any earlier reach.
  useEffect(() => {
    enabledRef.current = enabled;
    engagedRef.current = false;
    if (enabled && !hoveringRef.current) startPause();
    // A timeline dismissed mid-fade must not come up already faded the next time.
    return () => {
      clearTimers();
      setFading(false);
    };
  }, [enabled, startPause, clearTimers]);

  const noteActivity = useCallback(() => {
    if (!enabledRef.current || engagedRef.current || hoveringRef.current) return;
    setFading(false);
    startPause();
  }, [startPause]);

  const hover = useCallback((hovering: boolean) => {
    hoveringRef.current = hovering;
    if (!enabledRef.current || engagedRef.current) return;
    if (hovering) {
      clearTimers();
      setFading(false);
    } else {
      startPause();
    }
  }, [clearTimers, startPause]);

  const engage = useCallback(() => {
    if (engagedRef.current) return;
    engagedRef.current = true;
    clearTimers();
    setFading(false);
  }, [clearTimers]);

  return { fading: enabled && fading, noteActivity, hover, engage };
}
